import { MatNativeDateModule, ErrorStateMatcher } from '@angular/material/core';
import { ControlBaseComponent } from '../control-base/control-base.component';
import { FormsModule, NgControl, AbstractControl } from '@angular/forms';
import { MatTimepickerModule } from '@angular/material/timepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { StateService } from '../../../services/state.service';
import { MatInputModule } from '@angular/material/input';
import { Component, OnInit, Self } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';

class TimeErrorStateMatcher implements ErrorStateMatcher {
    constructor(private stateService: StateService) {}

    isErrorState(control: AbstractControl | null): boolean {
        if (!control) {
            return false;
        }

        if (this.stateService.formIsSubmitted) {
            return control.invalid;
        }

        if (control.errors) {
            return Object.keys(control.errors).some((key) => key !== 'required');
        }

        return false;
    }
}

@Component({
    selector: 'minster-time-picker',
    standalone: true,
    imports: [MatFormFieldModule, MatIconModule, MatInputModule, MatTimepickerModule, MatNativeDateModule, FormsModule],
    templateUrl: './minster-time-picker.component.html',
    styleUrl: './minster-time-picker.component.css'
})

export class MinsterTimePickerComponent extends ControlBaseComponent<string> implements OnInit {
    _time?: Date;
    errorStateMatcher: TimeErrorStateMatcher;

    constructor(@Self() public controlDir: NgControl, private stateService: StateService) {
        super(controlDir, stateService);

        this.errorStateMatcher = new TimeErrorStateMatcher(stateService);
    }

    override writeValue(value: any): void {
        if (value) {
            if (value === '00:00:00'  && this.stateService.claim?.accident.accidentDate == null) {
                this._time = undefined; 
            } 
            else {
                const date = new Date();
                const parts = value.split(':');
                date.setHours(parts[0], parts[1], parts[2]);
                this._time = date;
            }
        } 
        else {
            this._time = undefined; 
        }
    }

    ngOnInit(): void {
        this.setup();

        this.controlDir.control?.setValidators([
            this.timeFormatValidator.bind(this),
            this.requiredValidator.bind(this)
        ]);

        this.controlDir.control?.updateValueAndValidity();

        if (this.stateService.formIsSubmitted) {
            this.controlDir.control?.markAsTouched();
            this.controlDir.control?.markAsDirty();
        }
    }
    valueUpdated(time?: Date): void {
        if (time) {
            const hours = time.getHours();
            const mins = time.getUTCMinutes();

            if (hours === 0 && mins === 0  && this.stateService.claim?.accident.accidentDate == null) {
                this._propagateChange(null);
            } 
            else {
                const formattedTime = `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:00`;
                this._propagateChange(formattedTime);
            }
        } 
        else {
            this._propagateChange(null);
        }
    }

    get hasError(): boolean {
        return this.errorStateMatcher.isErrorState(this.controlDir?.control);
    }

    get errorMessage(): string {
        if (this.controlDir?.control?.hasError('required')) {
            return 'This field is required';
        }

        if (this.controlDir?.control?.hasError('invalidTimeFormat')) {
            return 'Invalid time format. Please use HH:mm format.';
        }

        return '';
    }
    
    private timeFormatValidator(control: AbstractControl): { [key: string]: boolean } | null {
        if (!control.value) {
            return null;
        }

        //Matches HH:mm:00 format
        const timeRegex = /^([01]\d|2[0-3]):([0-5]\d):00$/; 
        if (!timeRegex.test(control.value)) {
            return { invalidTimeFormat: true };
        }

        return null;
    }

    get isRequired(): boolean {
        return this.stateService.formIsSubmitted;
    }
    
    private requiredValidator(control: AbstractControl): { [key: string]: boolean } | null {
        if (this.stateService.formIsSubmitted && !control.value) {
            return { required: true };
        }
        
        return null;
    }
}